import { Building2, Settings, Shield, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { cn } from "@src/shared/lib/utils";
import { navigationV2 } from "@src/shared/navigation/v2";

import type { SettingsNavItemV2 } from "./SettingsLayout.types";

const settingsNavGroups: Array<{
  id: string;
  label: string;
  items: SettingsNavItemV2[];
}> = [
  {
    id: "personal",
    label: "Personal",
    items: [
      {
        id: "account",
        label: "Account",
        icon: User,
        href: navigationV2.settingsAccount(),
      },
      {
        id: "security",
        label: "Security",
        icon: Shield,
        href: navigationV2.settingsSecurity(),
      },
    ],
  },
  {
    id: "practice",
    label: "Practice",
    items: [
      {
        id: "practice-info",
        label: "Practice info",
        icon: Building2,
        href: navigationV2.settingsPracticeInfo(),
      },
    ],
  },
  {
    id: "preferences",
    label: "Preferences",
    items: [
      {
        id: "general",
        label: "General",
        icon: Settings,
        href: navigationV2.settingsPreferences(),
      },
    ],
  },
];

interface SettingsSidebarNavItemProps {
  item: SettingsNavItemV2;
  isActive: boolean;
}

function SettingsSidebarNavItem({
  item,
  isActive,
}: SettingsSidebarNavItemProps) {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      className={cn(
        "flex w-full items-center gap-2 rounded-[8px] px-[11px] py-[9px] text-left text-[13px] font-medium transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0",
        isActive
          ? "bg-primary/5 font-semibold text-primary"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
      aria-current={isActive ? "page" : undefined}
    >
      <Icon aria-hidden="true" className="size-[15px] shrink-0" />
      {item.label}
    </Link>
  );
}

export function SettingsSidebar() {
  const { pathname } = useLocation();

  return (
    <VerticalFlexbox
      as="aside"
      className="w-full shrink-0 border-b border-border bg-surface md-stitch:w-[220px] md-stitch:border-b-0 md-stitch:border-r"
      gap="none"
    >
      <VerticalFlexbox className="px-4 py-6 md-stitch:px-5" gap="none">
        <HeaderText tag="h1" className="text-[22px] tracking-tight">
          Settings
        </HeaderText>
      </VerticalFlexbox>
      <VerticalFlexbox
        aria-label="Settings"
        as="nav"
        className="px-3 pb-6 md-stitch:px-4"
        gap="none"
      >
        {settingsNavGroups.map((group) => (
          <VerticalFlexbox key={group.id} className="mb-5 last:mb-0" gap="none">
            <Text
              appearance="subdued"
              className="mb-1 px-[11px] text-[11px] font-medium uppercase tracking-[0.08em]"
            >
              {group.label}
            </Text>
            <VerticalFlexbox gap="none">
              {group.items.map((item) => (
                <SettingsSidebarNavItem
                  key={item.id}
                  item={item}
                  isActive={item.href === pathname}
                />
              ))}
            </VerticalFlexbox>
          </VerticalFlexbox>
        ))}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}
